import React from 'react';
import Dropzone from 'react-dropzone';

import classNames from 'classnames';

import { Icon } from '@opendoor/bricks';
import { MIME_TYPES } from '../../../constants/FileUploadConstants';
import { PermittedFileType } from '../../../declarations';
import SellerStatus from '../../resaleClosingDashboard/SellerStatus';
import { ISplitDocumentMetadata } from '../PdfSplittingModal';
import DownloadLink from './DownloadLink';
import FileInfo from './FileInfo';
import UploadButton from './UploadButton';

import './FileUploadStyle.less';

const ASSERT_NOT_NULL_ERROR = 'This file must be uploaded.';

export interface IFileUploadProps {
  acceptedFileTypes?: PermittedFileType[];
  downloadUrl?: string;
  file: File | null;
  displayName?: string | null;
  documentMetadata?: ISplitDocumentMetadata;
  hasSplitPdf?: boolean;
  maxSize?: number;
  minSize?: number;
  onDropAccepted?: (file: File, id: string, fileTypeId?: number | string) => void;
  onDropRejected?: (file: File[]) => void;
  onDeleteFile?: (id: string) => void;
  onEditPages?: (file: File) => void;
  required?: boolean;
  uniqueId: string;
  uploadFailed?: boolean;
  assertNotNull?: boolean;
}

export interface IFileUploadState {
  disableClick: boolean; // don't trigger the upload dialog when clicking Download or Delete
  showUploadButton: boolean;
}

export default class FileUpload extends React.Component<IFileUploadProps, IFileUploadState> {
  public static defaultProps: Partial<IFileUploadProps> = {
    acceptedFileTypes: [MIME_TYPES.pdf],
  };

  public state: IFileUploadState = {
    disableClick: false,
    showUploadButton: true,
  };

  public render(): JSX.Element {
    const {
      acceptedFileTypes,
      documentMetadata,
      downloadUrl,
      displayName,
      file,
      hasSplitPdf,
      maxSize,
      minSize,
      onDropRejected,
      required,
      uploadFailed,
      assertNotNull,
    } = this.props;

    const startPage = documentMetadata && documentMetadata.startPage;
    const endPage = documentMetadata && documentMetadata.endPage;
    return (
      <div className="file-upload">
        <Dropzone
          accept={!!acceptedFileTypes && acceptedFileTypes.join()}
          acceptClassName="accepted"
          activeClassName="active"
          className={classNames('upload-container', {
            error: uploadFailed || (assertNotNull && !file),
          })}
          disableClick={this.state.disableClick || !!file}
          onDropAccepted={this.onDroppedFileAccepted}
          onDropRejected={onDropRejected}
          maxSize={maxSize}
          minSize={minSize}
          rejectClassName="error"
        >
          {assertNotNull &&
            !file && (
              <div className="alert-error">
                <div className="error-text">
                  <strong>{ASSERT_NOT_NULL_ERROR}</strong>
                </div>
              </div>
            )}
          <div className="content">
            <div className="file-icon">
              {file ? <Icon name="file-pdf-blue-check" /> : <Icon name="file-pdf" />}
            </div>
            <div className="file-name-container">
              <div className="file-name">{displayName}</div>
              {downloadUrl &&
                !file && (
                  <DownloadLink
                    downloadUrl={downloadUrl}
                    onMouseOverLink={this.onMouseOverLink}
                    onMouseExitLink={this.onMouseExitLink}
                  />
                )}
              {file && (
                <FileInfo
                  displayName={file ? file.name : ''}
                  endPage={endPage}
                  fileSize={file && file.size}
                  hasFile={!!file}
                  hasSplitPdf={hasSplitPdf}
                  onEditPages={this.handleEditPages}
                  onMouseOverLink={this.onMouseOverLink}
                  onMouseExitLink={this.onMouseExitLink}
                  onRemoveFile={this.onRemoveFile}
                  startPage={startPage}
                />
              )}
              {uploadFailed && (
                <div className="status">
                  <SellerStatus
                    statusText="Upload failed. Please try again."
                    type="needs-attention"
                  />
                </div>
              )}
            </div>
            {!file && (
              <div className="upload">
                {this.state.showUploadButton && <UploadButton required={required} />}
              </div>
            )}
          </div>
        </Dropzone>
      </div>
    );
  }

  private handleEditPages = () => {
    const { file, onEditPages } = this.props;
    onEditPages && onEditPages(file);
  };

  private onDroppedFileAccepted = newFile => {
    if (!!this.props.file) {
      return;
    } else {
      this.setState({ showUploadButton: false });
    }
    this.props.onDropAccepted(newFile[0], this.props.uniqueId);
  };

  private onRemoveFile = () => {
    this.setState({
      disableClick: false,
      showUploadButton: true,
    });

    this.props.onDeleteFile(this.props.uniqueId);
  };

  private onMouseOverLink = () => this.setState({ disableClick: true });
  private onMouseExitLink = () => this.setState({ disableClick: false });
}
